import axios from "axios";

// ---------------------------------------------------------------------------
// Base URL resolution
//
// Single source of truth for the API base URL.
//   1. REACT_APP_API_URL from process.env if present (CRA injects at build).
//   2. Fall back to window.__API_URL__ if a host page injects it at runtime.
//   3. Last resort: same-origin "/api/" — keeps the app from posting to
//      "undefined/Auth/login" when the .env file is missing in a build.
//
// helpers/api_helper.js routes through THIS instance, so there is no
// second base URL anywhere in the app.
// ---------------------------------------------------------------------------
// const resolvedBaseURL =
//     (typeof process !== "undefined" && process.env && process.env.REACT_APP_API_URL) ||
//     (typeof window !== "undefined" && window.__API_URL__) ||
//     "https://localhost:7168/api/";



// Owner incident (2026-08-03) — this fallback silently sent EVERY request
// from local development to the LIVE PRODUCTION backend, which is why days
// of local fixes "didn't work": localhost was testing production's old code
// and writing test EPOs into the production database. The production URL is
// now reachable ONLY when the page is actually served from a non-local host;
// on localhost the fallback is the CRA proxy ("/api/" → package.json proxy →
// https://localhost:7168). REACT_APP_API_URL still wins when set — but note
// CRA reads .env ONLY at `npm start` launch, so restart after editing it.
//
// The non-local fallback is the Azure API App Service over HTTPS. It must
// stay https:// — the frontend is served over HTTPS, so an http:// value here
// is blocked by the browser as mixed content (symptom: "requested an insecure
// XMLHttpRequest endpoint ... has been blocked"). This fallback should only
// ever be reached if REACT_APP_API_URL is missing from the build; the value
// in .env.production is the intended source.
const isLocalHost =
    typeof window !== "undefined" &&
    ["localhost", "127.0.0.1", "[::1]"].includes(window.location.hostname);
// Access-Management hardening (Round 9, audit H-6): NO hardcoded production
// origin in code. Outside localhost the API base URL MUST come from
// REACT_APP_API_URL (.env.production, read at build time) or window.__API_URL__
// (runtime injection). If neither is set, requests go to a deliberately
// unreachable URL and a loud console error explains why, instead of silently
// posting credentials to a host chosen by the source code.
const configuredBaseURL =
    (typeof process !== "undefined" && process.env && process.env.REACT_APP_API_URL) ||
    (typeof window !== "undefined" && window.__API_URL__) ||
    null;
const resolvedBaseURL =
    configuredBaseURL || (isLocalHost ? "/api/" : "https://sswdashboard-prod-cus-api.azurewebsites.net/api/");
if (!configuredBaseURL && !isLocalHost) {
    console.error("[api] REACT_APP_API_URL is not set for this build — the dashboard cannot reach the API. " +
        "Set it in .env.production (https://…) and rebuild.");
}
// Transport (audit H-6): an https page must not call an http API (browsers block
// it as mixed content); an http page is itself the problem — say so.
if (typeof window !== "undefined" && !isLocalHost) {
    if (String(resolvedBaseURL).startsWith("http://")) {
        console.error("[api] The API base URL is plain http:// — credentials and tokens would travel unencrypted. Use https://.");
    }
    if (window.location.protocol === "http:") {
        console.error("[api] The dashboard is served over http:// — serve it over https:// before go-live.");
    }
}

const api = axios.create({
    baseURL: resolvedBaseURL,
    headers: { "Content-Type": "application/json" },
});


// Attach JWT + EmpId/Dept/Role on EVERY request (read fresh from

// ---------------------------------------------------------------------------
// Access-Management hardening — Round 8: SESSIONS
//
// The API now issues a SHORT access token (30 min) plus a refresh token. This
// module renews the session silently:
//   • before a request, if the access token expires within REFRESH_AHEAD_MS
//     the pair is refreshed first (proactive);
//   • on a 401 from a business endpoint, one refresh is attempted and the
//     request is retried once (reactive) — a 401 can also mean the session was
//     REVOKED server-side (password / role / status changed); the refresh then
//     fails for the same reason and the user is signed out.
// All concurrent callers share one in-flight refresh (single flight).
// Tokens are never written to the console.
// ---------------------------------------------------------------------------
const REFRESH_AHEAD_MS = 90 * 1000;
const NO_REFRESH_PATHS = ["auth/login", "auth/refresh", "auth/forgot-password", "auth/reset-password",
    "auth/password-policy", "auth/logout", "auth/setup-password"];
let refreshInFlight = null;

function readAuthUser() {
    try { return JSON.parse(sessionStorage.getItem("authUser") || "null"); } catch { return null; }
}
function writeAuthUser(next) {
    sessionStorage.setItem("authUser", JSON.stringify(next));
    const access = next?.access;
    if (access && typeof access === "object") sessionStorage.setItem("permissions", JSON.stringify(access));
}
function tokenExpiryMs(token) {
    try {
        const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
        return payload && payload.exp ? payload.exp * 1000 : null;
    } catch { return null; }
}
function isNoRefreshUrl(url) {
    const u = String(url || "").toLowerCase();
    return NO_REFRESH_PATHS.some((p) => u.includes(p));
}
export function signOutLocally(reason) {
    try { localStorage.removeItem("persist:authUser"); localStorage.removeItem("persist:permissions"); } catch { /* ignore */ }
    sessionStorage.removeItem("authUser");
    sessionStorage.removeItem("permissions");
    if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
        window.location.href = "/login" + (reason ? `?reason=${encodeURIComponent(reason)}` : "");
    }
}
export function refreshSession() {
    if (!refreshInFlight) {
        refreshInFlight = (async () => {
            const auth = readAuthUser();
            const refreshToken = auth?.refreshToken;
            if (!refreshToken) throw new Error("no refresh token");
            // Plain axios (not `api`) so this call never recurses into the interceptors.
            const res = await axios.post(resolvedBaseURL.replace(/\/?$/, "/") + "Auth/refresh",
                { refreshToken }, { headers: { "Content-Type": "application/json" } });
            const data = res.data || {};
            writeAuthUser({ ...auth, ...data });
            return data.token;
        })().finally(() => { refreshInFlight = null; });
    }
    return refreshInFlight;
}

// 🔐 Attach JWT + EmpId/Dept/Role on EVERY request (read fresh from

// sessionStorage each time — never cached at module load).
api.interceptors.request.use(async (config) => {
    // Round 8: proactive silent refresh when the access token is about to expire.
    try {
        if (!isNoRefreshUrl(config.url)) {
            const auth = readAuthUser();
            const exp = auth?.token ? tokenExpiryMs(auth.token) : null;
            if (auth?.refreshToken && exp && exp - Date.now() < REFRESH_AHEAD_MS) {
                try { await refreshSession(); } catch { /* fall through with the current token; the 401 path handles it */ }
            }
        }
    } catch { /* never block a request on refresh bookkeeping */ }
    try {
        const authData = sessionStorage.getItem("authUser");
        if (authData) {
            const parsed = JSON.parse(authData);

            const token = parsed?.token || parsed?.data?.token;
            if (token) config.headers.Authorization = `Bearer ${token}`;

            const empId = parsed?.uid || parsed?.data?.uid;
            if (empId) config.headers["X-EmpId"] = empId;

            const dept = parsed?.department || parsed?.data?.department;
            if (dept) config.headers["X-Dept"] = dept;

            const role = parsed?.role || parsed?.data?.role;
            if (role) config.headers["X-Role"] = role;

            // Merged from development branch — surface MPC flag for legacy parity
            const isMPC = parsed?.isMPC || parsed?.data?.isMPC;
            if (isMPC) config.headers["X-IsMPC"] = isMPC;
        }

        // When the body is FormData, NEVER let our JSON default leak through.
        // Axios+browser will set the correct 'multipart/form-data; boundary=...'
        // automatically as long as no Content-Type is present.
        // This protects Contract Review attachment uploads (and any future
        // multipart caller — EPO attachments, DMS attachments, etc.).
        if (typeof FormData !== "undefined" && config.data instanceof FormData) {
            if (config.headers && typeof config.headers.delete === "function") {
                config.headers.delete("Content-Type");
            } else if (config.headers) {
                delete config.headers["Content-Type"];
                delete config.headers["content-type"];
            }
        }
    } catch (err) {
        // Defensive: malformed sessionStorage shouldn't break the request.
        // Original logged here; preserved for parity.
        //test
        console.error("Error parsing authUser:", err);
    }
    return config;
});

// Round 8: reactive refresh — one retry after a 401 from a business endpoint.
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const status = error?.response?.status;
        const config = error?.config;
        if (status !== 401 || !config || config._retriedAfterRefresh || isNoRefreshUrl(config.url)) {
            return Promise.reject(error);
        }
        const auth = readAuthUser();
        if (!auth?.refreshToken) {
            // Legacy (pre-Round-8) session with no refresh token, or not signed in.
            if (auth?.token) signOutLocally("expired");
            return Promise.reject(error);
        }
        try {
            const newToken = await refreshSession();
            config._retriedAfterRefresh = true;
            config.headers = config.headers || {};
            config.headers.Authorization = `Bearer ${newToken}`;
            return api.request(config);
        } catch {
            signOutLocally("session");
            return Promise.reject(error);
        }
    }
);

// Exported so helpers/api_helper.js can route through the same instance.
// New code should still default to `import api from "../api"`.
export { api };
export default api;
